const HEX_COLOR = /^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const RGB_COLOR = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*(\d*\.?\d+)\s*)?\)$/i;
const IMPORTANT_FLAG = /!\s*important$/i;
const MAX_CHANNEL = 255;
const OPAQUE = 1;

interface RgbaColor {
  red: number;
  green: number;
  blue: number;
  alpha: number;
}

/** `#RGB`, `#RGBA`, `#RRGGBB` or `#RRGGBBAA`. */
function parseHexColor(value: string): RgbaColor | undefined {
  const [, digits] = HEX_COLOR.exec(value) ?? [];
  if (!digits) {
    return undefined;
  }
  const fullDigits = digits.length <= 4 ? [...digits].map((digit) => digit + digit).join('') : digits;
  const [red = 0, green = 0, blue = 0, alpha = MAX_CHANNEL] = (fullDigits.match(/../g) ?? []).map((pair) =>
    parseInt(pair, 16)
  );
  return { red, green, blue, alpha: alpha / MAX_CHANNEL };
}

/** `rgb(r, g, b)` or `rgba(r, g, b, a)`: the syntax browsers serialize and the mobile editor writes. */
function parseRgbColor(value: string): RgbaColor | undefined {
  const match = RGB_COLOR.exec(value);
  if (!match) {
    return undefined;
  }
  const [, redText, greenText, blueText, alphaText] = match;
  const [red, green, blue] = [redText, greenText, blueText].map((channel) => Math.min(Number(channel), MAX_CHANNEL));
  return { red, green, blue, alpha: alphaText === undefined ? OPAQUE : Number(alphaText) };
}

/**
 * A CSS colour as `#RRGGBB`, the one format the backend stores and every
 * client and the PDF read (PT-7251), or `undefined` when there is no colour to
 * keep. Same rules as the backend: alpha is dropped, a fully transparent
 * colour is no colour, names and other syntaxes are not read.
 */
export function normalizeTextColor(value: string): string | undefined {
  const color = parseHexColor(value.trim()) ?? parseRgbColor(value.trim());
  if (!color || color.alpha <= 0) {
    return undefined;
  }
  const hex = [color.red, color.green, color.blue].map((channel) => channel.toString(16).padStart(2, '0')).join('');
  return `#${hex}`.toUpperCase();
}

/**
 * The text colour an inline `style` gives, as `#RRGGBB`: as in the browser the
 * last `color` declaration wins; other properties do not matter.
 */
export function extractTextColor(style: string | null | undefined): string | undefined {
  const value = (style ?? '')
    .split(';')
    .map((declaration) => {
      const separatorIndex = declaration.indexOf(':');
      return separatorIndex < 0
        ? undefined
        : {
            property: declaration.slice(0, separatorIndex).trim().toLowerCase(),
            value: declaration.slice(separatorIndex + 1).trim().replace(IMPORTANT_FLAG, '').trim(),
          };
    })
    .filter((declaration) => declaration?.property === 'color')
    .pop()?.value;
  return value === undefined ? undefined : normalizeTextColor(value);
}

/** The inline style a coloured span is stored with. */
export function toTextColorStyle(color: string): string {
  return `color:${color}`;
}
